import { excludeDraftsFilter } from "./filters";
import * as Schema from "../types/schema";
import groq from "groq";
import { HeroTwoColSelection } from "@/components/PageBuilder/heroTwoColumn";
import { TypeFromSelection } from "groqd";
import { HeaderNavigationProps } from "@/components/HeaderNavigation";
import { SEOPageData } from "@/types/seoPageData";
import { FooterNavigationProps } from "@/components/FooterNavigation/query";

export type AboutUsData = {
  _id: string;
  pageFeatureFlag: Schema.FeatureFlag;
  givingBack: GivingBack;
  donatedValue?: number;
  press: Press;
  ourTeam: OurTeam;
  heroSection?: Schema.AboutUsHeroSection;
  hero?: TypeFromSelection<typeof HeroTwoColSelection>;
  companyOverview: CompanyOverviewProps;
  footerPayload: FooterNavigationProps;
} & SEOPageData &
  HeaderNavigationProps; // nav type added here to fix data types mismatch

export const getAboutUsQuery = (preview?: boolean) => `
  *[_type == "aboutUs"${!preview ? ` && ${excludeDraftsFilter}` : ""}]{
    _id,
    seo,
    pageFeatureFlag,
    heroSection,
    ourTeam{ 
      title, 
      subTitle,
      teamMembers[]{
        avatarDecoration, 
        "name": member->.name,
        "image": member->.image,
        "title": member->.title,
        "annotation": member->.annotation,
        "archetype": member->.archetype->{
          name, description, image
        }
      }
     },
    givingBack{
      organizations[]-> {
        _id,
        name,
        website,
        description,
        image,
      },
      percentageImage,
      bodyText,
      labelTotalDonated,
      learnMoreSection,
      reachOutToUsSection,
    },
    press{
      title,
      subTitle,
      companies[]-> {
        name,
        link,
        logo,
        articleName,
        articleLink
      }
    },
    "donatedValue":${getDonationInfo(preview)},
    hero {
      overline,
      heading,
      body,
      image,
      quickLinks,
      video-> {
        config,
        duration,
        posterImage,
        videoURL,
      }
    },
    companyOverview {
      _type,
      overline,
      title,
      subTitle,
      ctas[]
    },
  }
`;

export const getDonationInfo = (preview?: boolean): string => groq`
*[_type == "pricingLanding"${!preview ? ` && ${excludeDraftsFilter}` : ""}]{
  _id,
  "credit": moduleAdoptionTaxCredit.moduleInput.donation.donatedValue
}[0].credit
`;

export type GivingBack = {
  organizations: Array<Schema.SupportedFoundations>;
  percentageImage?: {
    _type: "image";
    asset: Schema.SanityReference<Schema.SanityImageAsset>;
    crop?: Schema.SanityImageCrop;
    hotspot?: Schema.SanityImageHotspot;
  };
  bodyText?: string;
  labelTotalDonated?: string;
  learnMoreSection?: string;
  reachOutToUsSection?: Schema.BlockContent;
};

export type Press = {
  title?: string;
  subTitle?: Schema.BlockContent;
  companies?: Array<Schema.Company>;
};

export type OurTeam = {
  title?: string;
  subTitle?: string;
  teamMembers?: Array<Person>;
};

export type Person = {
  title?: string;
  annotation?: string;
  archetype?: Schema.Archetype;
  name?: string;

  image?: {
    _type: "image";
    asset: Schema.SanityReference<Schema.SanityImageAsset>;
    crop?: Schema.SanityImageCrop;
    hotspot?: Schema.SanityImageHotspot;
  };

  avatarDecoration: DecorationImage;
};

export type DecorationImage = {
  image?: {
    asset: Schema.SanityReference<Schema.SanityImageAsset>;
    crop?: Schema.SanityImageCrop;
    hotspot?: Schema.SanityImageHotspot;
  };

  positionX?: string;

  positionY?: "top" | "bottom";
};

export type CompanyOverviewProps = {
  overline?: string;
  title?: string;
  subTitle?: string;
  ctas?: Array<{
    heading?: string;
    subtitle?: string;
    link?: string;
  }>;
};
